import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { RootState } from "../store";
import { clockActions } from "../store/clock-slice";
import { activeTabsActions } from "../store/tabs-slice";
import { calculateTotalTime } from "../utils/calculateTotalTime";
import { handleServiceEvent } from "../utils/handleServiceEvent";

export const useHandleService = (
  setIsModalOpen?: (arg: boolean) => void,
  setLoading?: (arg: boolean) => void,
  setIsModalStopped?: (arg: boolean) => void
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state: RootState) => state.user.currentUser.user);
  const appointment = useSelector((state: RootState) => state.appointment);
  const clockodoUser = useSelector(
    (state: RootState) => state.user.clockodoUser
  );

  const handleStartService = async (): Promise<void> => {
    if (!appointment.id || !user?.id) {
      return;
    }

    setIsModalOpen?.(false);
    setLoading?.(true);

    try {
      await handleServiceEvent(appointment, "start", clockodoUser, user);

      const { totalTime, lastEvent, isRunning, isPaused } =
        await calculateTotalTime(appointment.id, user.id);

      dispatch(
        clockActions.setClock({
          isRunning,
          appointmentId: appointment.id,
          employeeId: user.id,
          currentTime: totalTime,
          initialTime: totalTime,
          isPaused,
          lastEventTimestamp: lastEvent?.eventTimestamp ?? null,
        })
      );
    } catch (error) {
      console.log("Some error happened", error);
      throw error;
    } finally {
      setLoading?.(false);
    }
  };

  const handleStopService = async (): Promise<void> => {
    if (!appointment.id || !user?.id) {
      return;
    }

    setIsModalStopped?.(false);

    try {
      await handleServiceEvent(appointment, "stop", clockodoUser, user);

      dispatch(clockActions.resetClock(appointment.id));
      dispatch(activeTabsActions.setMonitorsStep(1));

      navigate(`/services/${appointment.id}/services-overview`);
    } catch (error) {
      console.log("Some error happened", error);
      throw error;
    }
  };

  const handleStopServiceAfter = async (): Promise<void> => {
    if (!appointment.id || !user?.id) {
      return;
    }

    setIsModalStopped?.(false);

    try {
      await handleServiceEvent(appointment, "stop", clockodoUser, user);

      dispatch(clockActions.resetClock(appointment.id));
      dispatch(activeTabsActions.setMonitorsStep(1));

      navigate(`/services/${appointment.id}/services-completion`);
    } catch (error) {
      console.log("Some error happened", error);
      throw error;
    }
  };

  const handlePauseService = async (): Promise<void> => {
    if (!appointment.id || !user?.id) {
      return;
    }

    try {
      await handleServiceEvent(appointment, "pause", clockodoUser, user);

      const { totalTime, lastEvent, isRunning, isPaused } =
        await calculateTotalTime(appointment.id, user.id);

      dispatch(
        clockActions.setClock({
          appointmentId: appointment.id,
          employeeId: user.id,
          currentTime: totalTime,
          initialTime: totalTime,
          isRunning,
          isPaused,
          lastEventTimestamp: lastEvent?.eventTimestamp ?? null,
        })
      );
    } catch (error) {
      console.log("Some error happened", error);
      throw error;
    }
  };

  const handleResumeService = async (): Promise<void> => {
    if (!appointment.id || !user?.id) {
      return;
    }

    try {
      await handleServiceEvent(appointment, "resume", clockodoUser, user);

      const { totalTime, lastEvent, isRunning, isPaused } =
        await calculateTotalTime(appointment.id, user.id);

      dispatch(
        clockActions.setClock({
          appointmentId: appointment.id,
          employeeId: user.id,
          isRunning,
          isPaused,
          currentTime: totalTime,
          initialTime: totalTime,
          lastEventTimestamp: lastEvent?.eventTimestamp ?? null,
        })
      );
    } catch (error) {
      console.log("Some error happened", error);
      throw error;
    }
  };

  return {
    handleStartService,
    handleStopService,
    handleStopServiceAfter,
    handlePauseService,
    handleResumeService,
  };
};
